/**
 * Explicit drawer-related query keys that don't follow suffix conventions.
 * These are set/removed as part of drawer open/close transitions.
 */
const DRAWER_ALLOWED_QUERY_KEYS = new Set([
  'drawerOrder',
  'drawerCompositeKey',
  'historyId',
  'integrationDrawerCategory',
  'threat-intel-drawer-tab',
  'cvss-version-selection',
  'assetOsint',
  'vulnerabilityOsint',
]);

/**
 * Suffix patterns that identify drawer-related query params.
 * Covers keys like assetDrawerKey, assetDrawerTab, assetDrawerSubTab, etc.
 */
const DRAWER_KEY_SUFFIXES = ['DrawerKey', 'DrawerTab', 'DrawerSubTab'];

const isDrawerRelatedKey = (key: string): boolean => {
  if (DRAWER_ALLOWED_QUERY_KEYS.has(key)) return true;

  return DRAWER_KEY_SUFFIXES.some(suffix => key.endsWith(suffix));
};

/**
 * Returns true if the search string transition contains at least one
 * drawer-related param change. Returns false if searches are identical.
 */
export const isRecognizedDrawerParamChange = (
  currentSearch: string,
  nextSearch: string
): boolean => {
  if (currentSearch === nextSearch) return false;

  const currentParams = new URLSearchParams(currentSearch);
  const nextParams = new URLSearchParams(nextSearch);

  const keys = new Set<string>();
  currentParams.forEach((_value, key) => keys.add(key));
  nextParams.forEach((_value, key) => keys.add(key));

  for (const key of keys) {
    const currentValue = currentParams.get(key);
    const nextValue = nextParams.get(key);
    const valueChanged = currentValue !== nextValue;

    if (!valueChanged) continue;

    if (isDrawerRelatedKey(key)) {
      return true;
    }
  }

  return false;
};

interface ShouldAllowDirtyQueryBuilderNavigationArgs {
  isDirty: boolean;
  currentPathname: string;
  nextPathname: string;
  currentSearch: string;
  nextSearch: string;
}

export const shouldAllowDirtyQueryBuilderNavigation = ({
  isDirty,
  currentPathname,
  nextPathname,
  currentSearch,
  nextSearch,
}: ShouldAllowDirtyQueryBuilderNavigationArgs): boolean => {
  if (!isDirty) return true;

  const pathChanged = currentPathname !== nextPathname;
  if (pathChanged) {
    return false;
  }

  const searchUnchanged = currentSearch === nextSearch;
  if (searchUnchanged) {
    return true;
  }

  return isRecognizedDrawerParamChange(currentSearch, nextSearch);
};
